import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { StoreIcon, FilterIcon, StarIcon, TagIcon, SearchIcon, MapPinIcon, CheckCircleIcon, LayoutGridIcon, LayoutListIcon } from 'lucide-react';
import BackButton from '../../components/UI/BackButton';
import SaveToFavorites from '../../components/UI/SaveToFavorites';
import { useDarkMode } from '../../context/DarkModeContext';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../../components/UI/FilterSidebar';
// Mock data for vendors
const vendors = [{
  id: '1',
  name: 'Literary Apparel Co.',
  slug: 'literary-apparel-co',
  avatar: 'https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1581318694548-0fb6e47fe59b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: 'Premium book-themed clothing and accessories for literary enthusiasts.',
  rating: 4.8,
  reviewCount: 245,
  location: 'Portland, OR',
  categories: ['Clothing', 'Accessories'],
  isOfficial: true,
  featuredAuthors: ['Elena Richards', 'Marcus Chen'],
  socialLinks: {
    website: 'https://literaryapparel.co',
    instagram: '@literaryapparel',
    twitter: '@literaryapparel'
  }
}, {
  id: '2',
  name: 'Cosmic Creations',
  slug: 'cosmic-creations',
  avatar: 'https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1579783902439-0f8a97d76c4f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: 'Handcrafted bookish merchandise with a cosmic twist.',
  rating: 4.9,
  reviewCount: 189,
  location: 'Austin, TX',
  categories: ['Art Prints', 'Home & Living'],
  isOfficial: true,
  featuredAuthors: ['Amara Knight'],
  socialLinks: {
    website: 'https://cosmiccreations.co',
    instagram: '@cosmiccreations'
  }
}, {
  id: '3',
  name: 'Bookish Treasures',
  slug: 'bookish-treasures',
  avatar: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1528396518501-b53845505208?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: 'Unique jewelry and accessories inspired by classic and contemporary literature.',
  rating: 4.7,
  reviewCount: 156,
  location: 'Boston, MA',
  categories: ['Jewelry', 'Accessories'],
  isOfficial: false,
  featuredAuthors: [],
  socialLinks: {
    instagram: '@bookishtreasures',
    etsy: '@BookishTreasuresCo'
  }
}, {
  id: '4',
  name: 'Novel Prints',
  slug: 'novel-prints',
  avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1513542789411-b6a5d4f31634?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: 'High-quality art prints featuring book quotes, covers, and literary-inspired designs.',
  rating: 4.6,
  reviewCount: 112,
  location: 'Seattle, WA',
  categories: ['Art Prints', 'Home Decor'],
  isOfficial: true,
  featuredAuthors: ['David Williams', 'Sarah Johnson'],
  socialLinks: {
    website: 'https://novelprints.com',
    instagram: '@novelprints'
  }
}, {
  id: '5',
  name: "Reader's Haven",
  slug: 'readers-haven',
  avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
  banner: 'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
  description: 'Cozy reading accessories, bookmarks, and reading lights for the perfect reading experience.',
  rating: 4.8,
  reviewCount: 203,
  location: 'Denver, CO',
  categories: ['Reading Accessories', 'Bookmarks', 'Home & Living'],
  isOfficial: false,
  featuredAuthors: [],
  socialLinks: {
    website: 'https://readershaven.co',
    instagram: '@readershaven'
  }
}];
type ViewMode = 'grid' | 'list';
const VendorDirectory = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [showVerified, setShowVerified] = useState(false);
  const [showOfficial, setShowOfficial] = useState(false);
  const [sortBy, setSortBy] = useState('name-asc');
  // Sidebar states
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const allCategories = Array.from(new Set(vendors.flatMap(vendor => vendor.categories))).sort();
  const filteredVendors = vendors.filter(vendor => {
    const matchesSearch = searchTerm === '' || vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) || vendor.description.toLowerCase().includes(searchTerm.toLowerCase()) || vendor.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategories = selectedCategories.length === 0 || selectedCategories.some(category => vendor.categories.includes(category));
    const matchesVerification = !showVerified || vendor.rating >= 4.5;
    const matchesOfficial = !showOfficial || vendor.isOfficial;
    return matchesSearch && matchesCategories && matchesVerification && matchesOfficial;
  });
  // Sort logic
  filteredVendors.sort((a, b) => {
    switch (sortBy) {
      case 'name-asc':
        return a.name.localeCompare(b.name);
      case 'name-desc':
        return b.name.localeCompare(a.name);
      case 'rating-desc':
        return b.rating - a.rating;
      case 'reviews-desc':
        return b.reviewCount - a.reviewCount;
      default:
        return 0;
    }
  });
  const toggleCategory = (category: string) => {
    setSelectedCategories(prev => prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]);
  };
  const resetFilters = () => {
    setSelectedCategories([]);
    setShowOfficial(false);
    setShowVerified(false);
    setSortBy('name-asc');
    setSearchTerm('');
  };
  const toggleMobileFilters = () => {
    setMobileFiltersOpen(!mobileFiltersOpen);
    document.body.style.overflow = !mobileFiltersOpen ? 'hidden' : 'auto';
  };
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };
  const filterSections: FilterSection[] = [{
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: [{
      value: 'name-asc',
      label: 'A-Z'
    }, {
      value: 'name-desc',
      label: 'Z-A'
    }, {
      value: 'rating-desc',
      label: 'Highest Rated'
    }, {
      value: 'reviews-desc',
      label: 'Most Reviews'
    }],
    value: sortBy,
    onChange: setSortBy
  }, {
    id: 'categories',
    title: 'Categories',
    type: 'checkbox',
    options: allCategories.map(cat => ({
      value: cat,
      label: cat
    })),
    value: selectedCategories,
    onChange: toggleCategory
  }, {
    id: 'verification',
    title: 'Verification',
    type: 'checkbox',
    options: [{
      value: 'official',
      label: 'Official Partners Only'
    }, {
      value: 'verified',
      label: 'Highly Rated (4.5+)'
    }],
    value: [...(showOfficial ? ['official'] : []), ...(showVerified ? ['verified'] : [])],
    onChange: val => {
      if (val === 'official') setShowOfficial(!showOfficial);
      if (val === 'verified') setShowVerified(!showVerified);
    }
  }];
  return <div className={`min-h-screen ${dk.page}`} data-id="element-22450" data-mp="2inmg68piy105">
      {/* Page Header */}
      <div className={`pt-24 md:pt-8 pb-6 px-4 sm:px-6 lg:px-8`} data-id="element-22451" data-mp="2nn91z6jf4pkk">
        <div className="max-w-7xl mx-auto" data-id="element-22452" data-mp="2nplirgdfwtw1">
          <div className="mb-4" data-id="element-22453" data-mp="28yma65clu5cg">
            <BackButton data-id="element-22454" data-mp="28xjme6akwj0r" />
          </div>
          <h1 className={`text-3xl font-bold mb-2 ${dk.textHeading}`} data-id="element-22455" data-mp="2dpb55unhtkgc">
            Merchandise Vendor Directory
          </h1>
          <p className={`text-lg max-w-2xl ${dk.textSecondary}`} data-id="element-22456" data-mp="1zz7odj7duiya">
            Discover official partners and unique creators offering literary
            merchandise for your readers.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12" data-id="element-22457" data-mp="1q0ifuh1dmaup">
        <div className="lg:hidden mb-6" data-id="element-22458" data-mp="1uzcztmb2y5ge">
          <button onClick={toggleMobileFilters} className={`w-full flex items-center justify-center px-4 py-3 border rounded-md transition-colors ${dk.card}`} data-id="element-22459" data-mp="1v164wx61ohma">
            <FilterIcon className="h-5 w-5 mr-2" data-id="element-22460" data-mp="16g1il4jsthc7" />
            <span data-id="element-22461" data-mp="1lbr5r60q00ii">Filters & Sort</span>
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8" data-id="element-22462" data-mp="1gce6tmwkr2zk">
          <FilterSidebarWrapper isOpen={mobileFiltersOpen} onClose={() => setMobileFiltersOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={toggleSidebar} data-id="element-22463" data-mp="1gb33tpaydz9i">
            <FilterSidebar title="Filters" sections={filterSections} onReset={resetFilters} data-id="element-22464" data-mp="241z47ath62sd" />
          </FilterSidebarWrapper>

          <main className="flex-1 min-w-0" data-id="element-22465" data-mp="1z4ywywkw2tzo">
            <div className={`rounded-xl shadow-sm p-4 mb-6 border ${dk.card}`} data-id="element-22466" data-mp="2d25aocqra2i1">
              <div className="flex flex-col md:flex-row md:items-center gap-4" data-id="element-22467" data-mp="291bpvs8m13td">
                <div className="relative flex-1" data-id="element-22468" data-mp="28z8oyd5x5q9v">
                  <SearchIcon className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${dk.textFaint}`} data-id="element-22469" data-mp="2obo3p3ibx4sx" />
                  <input type="text" placeholder="Search vendors by name, description, or location..." className={`pl-10 pr-4 py-2.5 w-full rounded-lg border focus:outline-none focus:ring-2 transition-colors ${rt.inputFocus} ${dk.inputOnCard}`} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-22470" data-mp="2ji3271hj1soz" />
                </div>
                <div className={`flex rounded-lg p-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} data-id="element-22471" data-mp="2xa9gsj517rmb">
                  <button onClick={() => setViewMode('grid')} className={`p-2 rounded-md transition-all ${viewMode === 'grid' ? darkMode ? `bg-gray-600 ${rt.textPrimary} shadow-sm` : `bg-white ${rt.textPrimary} shadow-sm` : dk.textMuted}`} aria-label="Grid view" data-id="element-22472" data-mp="2tbsa20piwj5f">
                    <LayoutGridIcon className="h-5 w-5" data-id="element-22473" data-mp="1dbqsi25of8e" />
                  </button>
                  <button onClick={() => setViewMode('list')} className={`p-2 rounded-md transition-all ${viewMode === 'list' ? darkMode ? `bg-gray-600 ${rt.textPrimary} shadow-sm` : `bg-white ${rt.textPrimary} shadow-sm` : dk.textMuted}`} aria-label="List view" data-id="element-22474" data-mp="6dg7a832d9ym">
                    <LayoutListIcon className="h-5 w-5" data-id="element-22475" data-mp="l0j6qkay5sol" />
                  </button>
                </div>
              </div>
            </div>

            <div className="mb-6" data-id="element-22476" data-mp="u2sc6vrcyvv4">
              <h2 className={`text-lg font-medium ${dk.textHeading}`} data-id="element-22477" data-mp="u1pqr3ct1lml">
                {filteredVendors.length} vendor
                {filteredVendors.length !== 1 ? 's' : ''} found
              </h2>
            </div>

            <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1 gap-4'}`} data-id="element-22478" data-mp="2vunlj4q56oa6">
              {filteredVendors.map(vendor => <Link key={vendor.id} to={`/vendor/${vendor.slug}`} className={`group rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl border ${darkMode ? 'bg-gray-800 shadow-lg border-gray-700' : 'bg-white shadow-md border-gray-100'}`} data-id="element-22479" data-mp="3u7szfucjkbuc">
                  {viewMode === 'grid' ? <>
                      <div className="relative h-40 bg-gray-200 overflow-hidden" data-id="element-22480" data-mp="uqtjfgynwaw4">
                        <img src={vendor.banner} alt={`${vendor.name} banner`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-22481" data-mp="usxjfntfwpr4" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-60" data-id="element-22482" data-mp="zrhag2mw4rqc"></div>
                        <div className="absolute top-3 right-3" data-id="element-22483" data-mp="1j9mzqosc5wm2">
                          <SaveToFavorites itemId={vendor.id} size="sm" className="bg-white/20 backdrop-blur-sm hover:bg-white/40 text-white" data-id="element-22484" data-mp="1o9hpgp0dkg5j" />
                        </div>
                      </div>
                      <div className="relative px-6 pt-12 pb-6" data-id="element-22485" data-mp="10jne6bvpezne">
                        <img src={vendor.avatar} alt={vendor.name} className={`absolute -top-10 left-6 w-20 h-20 rounded-xl object-cover shadow-lg border-4 ${darkMode ? 'border-gray-800' : 'border-white'}`} data-id="element-22486" data-mp="15c6omzz3henz" />
                        <div className="flex items-start justify-between mb-2" data-id="element-22487" data-mp="hnf50wzkm4mt">
                          <div data-id="element-22488" data-mp="loh3hblnycnq">
                            <h2 className={`text-xl font-bold ${dk.textPrimary}`} data-id="element-22489" data-mp="lnfsf74ndq60">
                              {vendor.name}
                            </h2>
                            <div className={`flex items-center text-xs mt-1 ${dk.textMuted}`} data-id="element-22490" data-mp="9nr95tla3vql">
                              <MapPinIcon className="h-3 w-3 mr-1" data-id="element-22491" data-mp="3w20u1sq02lmm" />
                              {vendor.location}
                            </div>
                          </div>
                          {vendor.isOfficial && <span className={`text-white text-[10px] uppercase font-bold px-2 py-1 rounded shadow-sm ${rt.badgeBg}`} data-id="element-22492" data-mp="3mz9fvl5qpbra">
                              Official
                            </span>}
                        </div>
                        <p className={`text-sm mb-4 line-clamp-2 ${dk.textSecondary}`} data-id="element-22493" data-mp="1g7qrw4bkipor">
                          {vendor.description}
                        </p>
                        <div className="flex items-center justify-between mt-auto" data-id="element-22494" data-mp="1ntdvy5audyku">
                          <div className="flex items-center text-sm" data-id="element-22495" data-mp="1nr9iowr1zn7w">
                            <div className="flex items-center text-yellow-500 mr-2" data-id="element-22496" data-mp="1skvu6llhdl24">
                              <StarIcon className="h-4 w-4 fill-current" data-id="element-22497" data-mp="1xi46w6nv0wvz" />
                              <span className={`ml-1 font-semibold ${dk.textPrimary}`} data-id="element-22498" data-mp="10ty5rtq5cmmg">
                                {vendor.rating}
                              </span>
                            </div>
                            <span className={`text-xs ${dk.textFaint}`} data-id="element-22499" data-mp="1esxuf1dnt1im">
                              ({vendor.reviewCount})
                            </span>
                          </div>
                          <div className="flex -space-x-2" data-id="element-22500" data-mp="2lbdwhkedrieh">
                            {vendor.categories.slice(0, 3).map((cat, i) => <div key={i} className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] border-2 ${darkMode ? 'bg-gray-700 border-gray-800 text-gray-300' : 'bg-gray-100 border-white text-gray-600'}`} title={cat} data-id="element-22501" data-mp="20j5y2vfbxcej" data-mpi={i}>
                                <TagIcon className="h-3 w-3" data-id="element-22502" data-mp="1ht7sclz07ve2" />
                              </div>)}
                          </div>
                        </div>
                      </div>
                    </> : <div className="p-6 flex flex-col sm:flex-row gap-6" data-id="element-22503" data-mp="1rkll8awg9wy8">
                      <div className="flex-shrink-0 relative w-full sm:w-48 h-32 rounded-lg overflow-hidden" data-id="element-22504" data-mp="1mriz2nu05ps2">
                        <img src={vendor.banner} alt={vendor.name} className="w-full h-full object-cover" data-id="element-22505" data-mp="z1eyyqg0w85t" />
                        <div className="absolute inset-0 bg-black/20" data-id="element-22506" data-mp="35pxftydl4b72"></div>
                        <img src={vendor.avatar} alt={vendor.name} className="absolute bottom-2 left-2 w-12 h-12 rounded-lg border-2 border-white shadow-md" data-id="element-22507" data-mp="3aj09e6id3kn9" />
                      </div>
                      <div className="flex-1 flex flex-col" data-id="element-22508" data-mp="3ohgig43p5n8k">
                        <div className="flex items-start justify-between" data-id="element-22509" data-mp="3tgt3qkex6m91">
                          <div data-id="element-22510" data-mp="3tg1hi077q0vf">
                            <div className="flex items-center gap-2" data-id="element-22511" data-mp="3fhblyp01k996">
                              <h2 className={`text-xl font-bold ${dk.textPrimary}`} data-id="element-22512" data-mp="3fj4jnesofj51">
                                {vendor.name}
                              </h2>
                              {vendor.isOfficial && <CheckCircleIcon className={`h-5 w-5 ${rt.textPrimary}`} title="Official Partner" data-id="element-22513" data-mp="2851cagjcdci4" />}
                            </div>
                            <p className={`text-sm ${dk.textMuted}`} data-id="element-22514" data-mp="2qr60hqfech86">
                              {vendor.location}
                            </p>
                          </div>
                          <SaveToFavorites itemId={vendor.id} size="sm" data-id="element-22515" data-mp="3ihslexwuq4n4" />
                        </div>
                        <p className={`mt-3 text-sm line-clamp-2 ${dk.textSecondary}`} data-id="element-22516" data-mp="3efp9k869sy1b">
                          {vendor.description}
                        </p>
                        <div className="mt-auto pt-4 flex items-center justify-between" data-id="element-22517" data-mp="34pcqkke3tcxa">
                          <div className="flex flex-wrap gap-2" data-id="element-22518" data-mp="6176ylz3map3">
                            {vendor.categories.map(category => <span key={category} className={`text-xs px-2 py-1 rounded-md flex items-center ${dk.badgeNeutral}`} data-id="element-22519" data-mp="1848bzfgsb21">
                                <TagIcon className="h-3 w-3 mr-1 opacity-70" data-id="element-22520" data-mp="iirg1x46r3in" />
                                {category}
                              </span>)}
                          </div>
                          <div className="flex items-center text-sm font-medium text-yellow-500" data-id="element-22521" data-mp="rjy02g61mwgn">
                            <StarIcon className="h-4 w-4 fill-current mr-1" data-id="element-22522" data-mp="whzegnsiy7ls" />
                            {vendor.rating}
                            <span className={`ml-1 font-normal ${dk.textFaint}`} data-id="element-22523" data-mp="11ak89pbfkkpb">
                              ({vendor.reviewCount})
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>}
                </Link>)}
            </div>

            {filteredVendors.length === 0 && <div className={`text-center py-16 rounded-xl border-2 border-dashed ${dk.emptyState}`} data-id="element-22524" data-mp="2l4fn9o93hi68">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} data-id="element-22525" data-mp="2p5j5uiij1sb5">
                  <StoreIcon className={`h-8 w-8 ${dk.textFaint}`} data-id="element-22526" data-mp="2z59zkhz9k3a6" />
                </div>
                <h3 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-22527" data-mp="3431ulkv7g5il">
                  No vendors found
                </h3>
                <p className={dk.textMuted} data-id="element-22528" data-mp="38utljwi9wd99">
                  Try adjusting your search terms or selected filters
                </p>
                <button onClick={resetFilters} className={`mt-4 font-medium text-sm ${rt.accentLink}`} data-id="element-22529" data-mp="3cxpzh9wvbl6v">
                  Clear all filters
                </button>
              </div>}
          </main>
        </div>
      </div>
    </div>;
};
export default VendorDirectory;